import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useGmbPosts,
  useGmbPhotos,
  useGmbVideos,
  useUpdatePost,
  usePublishPost,
  useSchedulePost,
  useEditPostImage,
  useGeneratePostImageDescription,
  useUploadPhoto,
  useUploadVideo,
} from '../../hooks/useGmbImplementation';
import { useGmbConnectionStatus } from '../../hooks/useGmbStrategy';
import { gmbApi } from '@/services/gmbApi';
import { loadVideoMetadata, extractVideoThumbnail } from './videoUpload';

interface PostSchedulerProps {
  onTooltipShow: (title: string, content: string) => void;
}

const ctaOptions = [
  'Call Now',
  'Learn More',
  'Book Now',
  'Refer Now',
  'View Details',
  'RSVP',
  'Claim Offer',
  'Watch Video',
];

const postTypeColors: Record<string, string> = {
  offer: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  whats_new: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
  event: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300',
};

function formatPostType(type: string): string {
  const map: Record<string, string> = {
    whats_new: "What's New",
    offer: 'Offer',
    event: 'Event',
  };
  return map[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCta(cta: string): string {
  return cta.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const INTENT_LABELS: Record<string, string> = {
  introduction: 'Business Introduction/Awareness',
  educational: 'Educational Tips',
  offer: 'Promotional Offer',
  testimonial: 'Customer Testimonial',
  product: 'Product Highlight',
  behind_scenes: 'Behind the Scenes',
  event: 'Event/Seasonal',
};
function formatIntent(intent: string): string {
  if (!intent) return '—';
  return INTENT_LABELS[intent] || intent.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(dateStr: string): string {
  if (!dateStr) return 'Not scheduled';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

type FilterStatus = 'All' | 'Published' | 'Draft';

// Image descriptions and AI image edits apply to photos only.
function isVideoUrl(url: string): boolean {
  return /\.(mp4|mov|m4v|webm|avi|mkv)(\?|$)/i.test(url || '');
}

export default function PostScheduler({ onTooltipShow }: PostSchedulerProps) {
  const [historyFilter, setHistoryFilter] = useState<FilterStatus>('All');
  const [historyEditId, setHistoryEditId] = useState<string | null>(null);
  const [historyEditTitle, setHistoryEditTitle] = useState('');
  const [historyEditContent, setHistoryEditContent] = useState('');
  const [historyEditType, setHistoryEditType] = useState('');
  const [historyEditCTA, setHistoryEditCTA] = useState('');
  const [historyEditKeyword, setHistoryEditKeyword] = useState('');
  const [historyEditImageUrl, setHistoryEditImageUrl] = useState('');
  const [historyEditImageDesc, setHistoryEditImageDesc] = useState('');
  const [aiEditPrompt, setAiEditPrompt] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const galleryFileInputRef = useRef<HTMLInputElement | null>(null);
  const generatedDescriptionKey = useRef('');

  const { data: postsData, isLoading, error, refetch } = useGmbPosts();
  const { data: photosData } = useGmbPhotos();
  const { data: videosData } = useGmbVideos();
  const { data: connectionData } = useGmbConnectionStatus();
  const updatePostMutation = useUpdatePost();
  const publishPostMutation = usePublishPost();
  const schedulePostMutation = useSchedulePost();
  const editPostImageMutation = useEditPostImage();
  const uploadPhotoMutation = useUploadPhoto();
  const uploadVideoMutation = useUploadVideo();
  const {
    mutateAsync: mutateGenerateImageDescription,
    isPending: isGeneratingImageDescription,
  } = useGeneratePostImageDescription();

  const businessName = connectionData?.business_name || 'Your Business';

  const posts = postsData?.data?.posts || [];
  const galleryPhotos = (photosData?.data?.photos || []).filter(
    (p: any) => p.photo_type !== 'video'
  );
  const galleryVideos = videosData?.data?.videos || [];
  const publishedCount = postsData?.data?.published_count || 0;
  const draftsCount = postsData?.data?.drafts_count || 0;
  const totalCount = postsData?.data?.total_count || 0;

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }, []);

  const generateImageDescription = useCallback(async (postId: string, imageUrl: string) => {
    if (isVideoUrl(imageUrl)) return;
    try {
      const result = await mutateGenerateImageDescription({ postId, imageUrl });
      const description = result?.image_description || result?.data?.image_description;
      if (!description) throw new Error('The image description service returned no description.');
      setHistoryEditImageDesc(description);
    } catch (err: any) {
      showToast(err.message || 'Image description generation failed');
    }
  }, [mutateGenerateImageDescription, showToast]);

  useEffect(() => {
    if (!historyEditId || !historyEditImageUrl || historyEditImageDesc.trim()) return;
    if (isVideoUrl(historyEditImageUrl)) return;
    const generationKey = `${historyEditId}:${historyEditImageUrl}`;
    if (generatedDescriptionKey.current === generationKey) return;
    generatedDescriptionKey.current = generationKey;
    void generateImageDescription(historyEditId, historyEditImageUrl);
  }, [historyEditId, historyEditImageUrl, historyEditImageDesc, generateImageDescription]);

  // Swapping the image (gallery pick, upload, AI edit, remove) makes the old
  // description wrong; clearing it lets the effect above describe the new one.
  const changeEditImage = useCallback((url: string) => {
    setHistoryEditImageUrl(url);
    setHistoryEditImageDesc('');
  }, []);

  const filteredPosts = posts.filter((post: any) =>
    historyFilter === 'All' ? true : post.status === historyFilter.toLowerCase()
  );

  const stats = [
    { label: 'Published', value: publishedCount, icon: 'ri-check-double-line', filter: 'Published' as FilterStatus },
    { label: 'Drafts', value: draftsCount, icon: 'ri-draft-line', filter: 'Draft' as FilterStatus },
    { label: 'Total Posts', value: totalCount, icon: 'ri-file-list-line', filter: 'All' as FilterStatus },
  ];

  const handleEditHistory = (post: any) => {
    setHistoryEditKeyword(post.keyword_used || '');
    setHistoryEditId(post.id);
    setHistoryEditTitle(post.title || '');
    setHistoryEditContent(post.caption || '');
    setHistoryEditType(post.google_post_type || 'whats_new');
    setHistoryEditCTA(post.cta_type || 'call_now');
    setHistoryEditImageUrl(post.image_url || '');
    setHistoryEditImageDesc(post.image_description || '');
    setAiEditPrompt('');
  };

  const handleSaveHistoryEdit = async () => {
    if (historyEditId == null) return;
    try {
      await updatePostMutation.mutateAsync({
        postId: historyEditId,
        data: {
          title: historyEditTitle,
          caption: historyEditContent,
          cta_type: historyEditCTA,
          image_url: historyEditImageUrl,
          image_description: historyEditImageDesc,
        },
      });
      setHistoryEditId(null);
      showToast('Post updated');
    } catch (err: any) {
      showToast(err.message || 'Update failed');
    }
  };

  const handleCancelHistoryEdit = () => {
    setHistoryEditId(null);
    setHistoryEditTitle('');
    setHistoryEditContent('');
    setHistoryEditType('');
    setHistoryEditCTA('');
    setHistoryEditKeyword('');
    setHistoryEditImageUrl('');
    setHistoryEditImageDesc('');
    setAiEditPrompt('');
  };

  const handlePublish = async (postId: string) => {
    try {
      await publishPostMutation.mutateAsync(postId);
      showToast('Post published');
    } catch (err: any) {
      showToast(err.message || 'Publish failed');
    }
  };

  const handleSchedule = async (postId: string) => {
    try {
      await schedulePostMutation.mutateAsync(postId);
      showToast('Post scheduled');
    } catch (err: any) {
      showToast(err.message || 'Schedule failed');
    }
  };

  const handleDownload = async (post: any) => {
    if (downloadingId != null) return;
    setDownloadingId(post.id);
    try {
      const userId = localStorage.getItem('user_id') || '';
      const { blob } = await gmbApi.downloadPost(userId, post.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `post_week_${post.week_number ?? 'x'}_post_${post.post_number ?? 'x'}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast('Post downloaded');
    } catch (err: any) {
      showToast(err.message || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleAiEditImage = async () => {
    if (historyEditId == null || !aiEditPrompt.trim()) return;
    if (isVideoUrl(historyEditImageUrl)) {
      showToast('AI image edit is only available for images');
      return;
    }
    try {
      const result = await editPostImageMutation.mutateAsync({
        postId: historyEditId,
        prompt: aiEditPrompt.trim(),
        imageUrl: historyEditImageUrl,
      });
      const newUrl = result?.image_url || result?.data?.image_url || '';
      if (newUrl) changeEditImage(newUrl);
      setAiEditPrompt('');
      showToast('AI image edit complete');
    } catch (err: any) {
      showToast(err.message || 'AI image edit failed');
    }
  };

  const handleGalleryFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || historyEditId == null) return;
    const userId = localStorage.getItem('user_id') || '';

    if (file.type.startsWith('video/')) {
      try {
        const { seconds, resolution } = await loadVideoMetadata(file);
        const thumbnail = await extractVideoThumbnail(file);

        const formData = new FormData();
        formData.append('user_id', userId);
        formData.append('file', file);
        if (thumbnail) formData.append('thumbnail', thumbnail, `${file.name}_thumb.jpg`);
        if (seconds != null)
          formData.append(
            'duration_seconds',
            String(Math.max(1, Math.min(120, Math.round(seconds))))
          );
        if (resolution) formData.append('video_resolution', resolution);

        const result = await uploadVideoMutation.mutateAsync(formData);
        const url = result?.data?.s3_url || result?.s3_url || '';
        if (url) changeEditImage(url);
        showToast('Media uploaded and attached');
      } catch (err: any) {
        showToast(err.message || 'Upload failed');
      }
      return;
    }

    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('file', file);
    formData.append('photo_type', 'post');
    try {
      const result = await uploadPhotoMutation.mutateAsync(formData);
      const url = result?.s3_url || result?.data?.s3_url || '';
      if (url) changeEditImage(url);
      showToast('Media uploaded and attached');
    } catch (err: any) {
      showToast(err.message || 'Upload failed');
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
        <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">Failed to load posts</h3>
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error.message}</p>
        <button onClick={() => refetch()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-3"></div>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-check-line text-green-400"></i>
          {toast}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {stats.map((stat) => (
          <button
            key={stat.label}
            onClick={() => setHistoryFilter(stat.filter)}
            className={`text-left bg-white dark:bg-gray-800 rounded-lg border p-5 shadow-sm transition-all cursor-pointer ${
              historyFilter === stat.filter
                ? 'border-teal-500 ring-1 ring-teal-500'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center text-gray-700 dark:text-gray-300">
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.label}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
          </button>
        ))}
      </div>

      {/* Generated Posts History */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Generated Posts History</h4>
            <button
              onClick={() => onTooltipShow('Post Scheduler', 'View and manage your GMB posts. Posts approved from your Content Calendar (Action Plan) land here for scheduling and publishing. Edit drafts, publish posts, and download content for manual posting.')}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="More info"
            >
              <i className="ri-information-line text-lg"></i>
            </button>
          </div>
          {historyFilter !== 'All' && (
            <button
              onClick={() => setHistoryFilter('All')}
              className="text-[10px] text-teal-600 dark:text-teal-400 font-medium hover:underline cursor-pointer"
            >
              Clear filter
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2 pr-3">Date</th>
                <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2 pr-3">Content Intent</th>
                <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2 pr-3">Type</th>
                <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2 pr-3">Title</th>
                <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2 pr-3">CTA</th>
                <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2 pr-3">Keyword</th>
                <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2 pr-3">Status</th>
                <th className="text-left font-medium text-gray-500 dark:text-gray-400 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.map((post: any) => (
                <tr key={post.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {post.scheduled_date ? formatDate(post.scheduled_date) : <span className="text-gray-400 dark:text-gray-500">Not scheduled</span>}
                  </td>
                  <td className="py-2 pr-3 text-gray-500 dark:text-gray-400 max-w-[160px] truncate" title={formatIntent(post.content_intent)}>{formatIntent(post.content_intent)}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${postTypeColors[post.google_post_type] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                      {formatPostType(post.google_post_type)}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={post.title}>{post.title || 'Untitled'}</td>
                  <td className="py-2 pr-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{post.cta_type ? formatCta(post.cta_type) : '—'}</td>
                  <td className="py-2 pr-3 text-gray-500 dark:text-gray-400 max-w-[160px] truncate" title={post.keyword_used}>{post.keyword_used || '—'}</td>
                  <td className="py-2 pr-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        post.status === 'scheduled'
                          ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                          : post.status === 'published'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      <i
                        className={`${
                          post.status === 'scheduled'
                            ? 'ri-calendar-schedule-line'
                            : post.status === 'published'
                              ? 'ri-check-line'
                              : 'ri-edit-line'
                        }`}
                      ></i>
                      {post.status === 'scheduled' ? 'Scheduled' : post.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditHistory(post)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-edit-line"></i>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDownload(post)}
                        disabled={downloadingId === post.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {downloadingId === post.id ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : (
                          <i className="ri-download-line"></i>
                        )}
                        Download
                      </button>
                      {post.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleSchedule(post.id)}
                            disabled={
                              schedulePostMutation.isPending &&
                              schedulePostMutation.variables === post.id
                            }
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-orange-400 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Queue on the next default cadence date"
                          >
                            {schedulePostMutation.isPending &&
                            schedulePostMutation.variables === post.id ? (
                              <i className="ri-loader-4-line animate-spin"></i>
                            ) : (
                              <i className="ri-calendar-schedule-line"></i>
                            )}
                            {schedulePostMutation.isPending &&
                            schedulePostMutation.variables === post.id
                              ? 'Saving...'
                              : 'Schedule'}
                          </button>
                        </>
                      )}
                      {(post.status === 'draft' || post.status === 'scheduled') && (
                        <button
                          onClick={() => handlePublish(post.id)}
                          disabled={
                            publishPostMutation.isPending &&
                            publishPostMutation.variables === post.id
                          }
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {publishPostMutation.isPending &&
                          publishPostMutation.variables === post.id ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              Publishing...
                            </>
                          ) : (
                            <>
                              <i className="ri-check-line"></i>
                              Publish
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredPosts.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-xs text-gray-400 dark:text-gray-500">
                    No posts found for the selected filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit History Post Modal */}
      {historyEditId != null && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-12">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Edit Post</h3>
              <button
                onClick={handleCancelHistoryEdit}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* CTA selection */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">CTA</label>
              <select
                value={historyEditCTA}
                onChange={(e) => setHistoryEditCTA(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {ctaOptions.map((cta) => (
                  <option key={cta} value={cta.toLowerCase().replace(/\s+/g, '_')}>{cta}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Options pulled from the Action Plan content calendar.</p>
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
              <input
                type="text"
                value={historyEditTitle}
                onChange={(e) => setHistoryEditTitle(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Attached image */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Attached Image
              </label>
              {historyEditImageUrl ? (
                <div className="relative">
                  {historyEditImageUrl.toLowerCase().includes('.mp4') ? (
                    <div className="w-full max-h-48 flex flex-col items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 py-6">
                      <i className="ri-video-line text-3xl mb-1"></i>
                      <span className="text-[11px]">Video attached</span>
                    </div>
                  ) : (
                    <img
                      src={historyEditImageUrl}
                      alt="Attached post image"
                      className="w-full max-h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  )}
                  <button
                    onClick={() => changeEditImage('')}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-gray-900/70 text-white flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                    title="Remove image"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-4 text-center text-xs border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500">
                  <i className="ri-image-line text-xl mb-1 block"></i>
                  No image attached
                </div>
              )}
            </div>

            {/* Image description */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Image Description
                </label>
                {historyEditImageUrl && !isVideoUrl(historyEditImageUrl) && (
                  <button
                    type="button"
                    onClick={() => {
                      generatedDescriptionKey.current = '';
                      void generateImageDescription(historyEditId, historyEditImageUrl);
                    }}
                    disabled={isGeneratingImageDescription}
                    className="text-[11px] font-medium text-teal-600 hover:text-teal-700 disabled:opacity-50"
                  >
                    {isGeneratingImageDescription ? 'Generating...' : 'Generate with AI'}
                  </button>
                )}
              </div>
              <textarea
                value={historyEditImageDesc}
                onChange={(e) => setHistoryEditImageDesc(e.target.value)}
                rows={2}
                placeholder={historyEditImageUrl ? 'Generating a keyword-aware description...' : 'Attach an image to generate a description.'}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Image picker */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select from gallery
              </label>
              {(galleryPhotos.length > 0 || galleryVideos.length > 0) ? (
                <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                  {galleryPhotos.map((photo: any) => (
                    <button
                      key={photo.id}
                      onClick={() => changeEditImage(photo.s3_url || '')}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        historyEditImageUrl && historyEditImageUrl === photo.s3_url
                          ? 'border-teal-500 ring-1 ring-teal-500'
                          : 'border-transparent hover:border-gray-400'
                      }`}
                      title={photo.file_name}
                    >
                      <img
                        src={photo.s3_url || ''}
                        alt={photo.file_name || 'Gallery photo'}
                        className="w-full h-16 object-cover"
                      />
                    </button>
                  ))}
                  {galleryVideos.map((video: any) => (
                    <button
                      key={video.id}
                      onClick={() => changeEditImage(video.s3_url || '')}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        historyEditImageUrl && historyEditImageUrl === video.s3_url
                          ? 'border-teal-500 ring-1 ring-teal-500'
                          : 'border-transparent hover:border-gray-400'
                      }`}
                      title={video.file_name}
                    >
                      <div className="w-full h-16 flex flex-col items-center justify-center bg-gray-900 text-white">
                        <i className="ri-video-line text-xl"></i>
                        <span className="text-[9px] mt-0.5">VIDEO</span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  No uploaded media to choose from. Add photos or videos below first.
                </p>
              )}

              <input
                ref={galleryFileInputRef}
                type="file"
                accept="image/*,video/mp4,.mp4"
                className="hidden"
                onChange={handleGalleryFileChange}
              />
              <button
                onClick={() => galleryFileInputRef.current?.click()}
                disabled={uploadPhotoMutation.isPending || uploadVideoMutation.isPending}
                className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer text-xs"
              >
                {uploadPhotoMutation.isPending || uploadVideoMutation.isPending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-2-line"></i>
                    Upload photo / video
                  </>
                )}
              </button>
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">
                New image uploads are saved as <span className="font-medium text-gray-500 dark:text-gray-400">Posts</span> type
                (1200 × 900 px). Videos are saved as Video type.
              </p>
            </div>

            {/* AI image edit */}
            {historyEditImageUrl && (
              <div className="rounded-lg border border-purple-200 dark:border-purple-900/50 bg-purple-50 dark:bg-purple-900/10 p-3">
                <label className="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                  Edit image with AI
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={aiEditPrompt}
                    onChange={(e) => setAiEditPrompt(e.target.value)}
                    placeholder={
                      historyEditImageUrl.toLowerCase().includes('.mp4')
                        ? 'Not available for videos'
                        : 'e.g. Make this a cozy, warm scene'
                    }
                    disabled={historyEditImageUrl.toLowerCase().includes('.mp4')}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  />
                  <button
                    onClick={handleAiEditImage}
                    disabled={!aiEditPrompt.trim() || editPostImageMutation.isPending || historyEditImageUrl.toLowerCase().includes('.mp4')}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editPostImageMutation.isPending ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Generating...
                      </>
                    ) : (
                      <>
                        <i className="ri-sparkling-2-line"></i>
                        Generate
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[10px] text-purple-500 dark:text-purple-400 mt-1.5">
                  AI image edit is only available for images, not videos.
                </p>
              </div>
            )}

            {/* Content */}
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Content</label>
              <textarea
                value={historyEditContent}
                onChange={(e) => setHistoryEditContent(e.target.value)}
                rows={8}
                maxLength={2000}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <div className="text-right text-xs text-gray-400 dark:text-gray-500 mt-1">{historyEditContent.length}/2000</div>

              {historyEditKeyword && (
                <div className="mt-3">
                  <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                    Keyword
                  </label>
                  <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md px-3 py-2.5 text-sm text-gray-600 dark:text-gray-400 select-none cursor-not-allowed">
                    {historyEditKeyword}
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">Keyword is auto-generated and must remain in the caption.</p>
                </div>
              )}
            </div>

            {/* GMB Post Preview */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
              <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-4 py-2.5 flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold">
                  {businessName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-800 dark:text-white">{businessName}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">Just now</p>
                </div>
              </div>

              {historyEditImageUrl ? (
                <div className="relative">
                  {historyEditImageUrl.toLowerCase().includes('.mp4') ? (
                    <div className="w-full h-52 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      <i className="ri-video-line text-3xl mb-1"></i>
                      <span className="text-[10px]">Video attached</span>
                    </div>
                  ) : (
                    <img src={historyEditImageUrl} alt="Post preview" className="w-full h-52 object-cover" />
                  )}
                </div>
              ) : (
                <div className="w-full h-36 bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-300 dark:text-gray-600">
                  <div className="text-center">
                    <i className="ri-image-line text-2xl"></i>
                    <p className="text-[10px] mt-1">No image selected</p>
                  </div>
                </div>
              )}

              <div className="p-4 space-y-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                  {historyEditTitle || 'Post Title'}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line">
                  {historyEditContent || 'Post content will appear here...'}
                </p>
                {historyEditKeyword && (
                  <p className="text-[10px] text-teal-600 dark:text-teal-400 font-medium">
                    {historyEditKeyword}
                  </p>
                )}
              </div>

              <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                  <span className={`inline-block w-2 h-2 rounded-full ${historyEditType === 'offer' ? 'bg-orange-400' : historyEditType === 'event' ? 'bg-rose-400' : 'bg-teal-400'}`}></span>
                  {formatPostType(historyEditType) || 'Post'}
                </div>
                <span className="px-4 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-md">
                  {formatCta(historyEditCTA) || 'Call Now'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSaveHistoryEdit}
                disabled={updatePostMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                {updatePostMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={handleCancelHistoryEdit}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
